import React from 'react';
import DataGrid from '@gogame/data-grid';

import { NumberValueFormatter } from '@gogame/intl-ts';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenState,
  useScreenDispatch,
  useScreenTitle,
} from '@gogame/screens';

import {
  PublicPromoCodeFormEdit,
  PromoCodeSetFormEdit,
  PublicPromoCodeFormDuplicate,
} from 'scenes/PromoCodes/components/PromoCodesForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_TYPE,
  COLUMN_ACTIVITY_TYPE,
  COLUMN_ATTRIBUTION,
  COLUMN_PLATFORMS,
  COLUMN_GG_CAMPAIGNS,
  COLUMN_ACTIVITY_PERIOD,
  COLUMN_REDEEMED,
  COLUMN_APPLICATION,
  COLUMN_UPDATED_BY,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COLUMN_REQUESTED_BY,
  COLUMN_USAGE_LIMIT,
  COLUMN_EXCLUDED_COUNTRIES,
  COLUMN_COUNTRIES,
  COLUMN_CONDITION_REWARDS,
} from 'scenes/PromoCodes/constants';
import { useLoadPromoCodesGrid } from 'scenes/PromoCodes/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/PromoCodes/screenReducer';

import {
  STATUS_ACTIVE,
  STATUS_PENDING,
  STATUS_EXPIRED,
  STATUS_INACTIVE,
} from 'scenes/PromoCodes/constants';

import ScreenLinkToTracker from 'scenes/PromoCodes/components/ScreenLinkToTracker';
import { usePeriodForScreenTracker } from 'scenes/PromoCodes/hooks';

import { NameColumn } from './Columns';
import Toolbar from './Toolbar';
import PromoCodesActions from 'scenes/PromoCodes/components/Actions';

const getColor = (id) => {
  switch (id) {
    case STATUS_ACTIVE:
      return 'success';
    case STATUS_INACTIVE:
      return 'draft';
    case STATUS_PENDING:
      return 'pending';
    case STATUS_EXPIRED:
      return 'draft';
  }
};

const PromoCodesDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();

  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const { filters, tableSettings, searchQuery, ready } = screenState;

  const periodForScreenTracker = usePeriodForScreenTracker();

  const { data, isLoading, isRefetching } = useLoadPromoCodesGrid(
    { filters, tableSettings, searchQuery },
    {
      enabled: ready,
    }
  );

  const handleDuplicateAction = async ({ id }) => {
    openChildComponent(PublicPromoCodeFormDuplicate, {
      id,
    });
  };

  const handleEditPublicAction = async ({ id }) => {
    openChildComponent(PublicPromoCodeFormEdit, {
      id,
    });
  };

  const handleEditSetAction = async ({ id }) => {
    openChildComponent(PromoCodeSetFormEdit, {
      id,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'promoCodes'}
        defaultColumnWidth={120}
        rowHeight={90}
        ready={ready}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={70}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Name'}
          getStatusColor={getColor}
          width={460}
          fixed={true}
          resizable={false}
          renderCell={({ value, row }) => (
            <NameColumn value={value} row={row} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={94}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_TYPE}
          type={'text'}
          headerName={'Type'}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_ACTIVITY_TYPE}
          type={'text'}
          headerName={'Activity Type'}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_ATTRIBUTION}
          type={'text'}
          headerName={'Attribution'}
          width={96}
        />
        <DataGrid.Column
          field={COLUMN_USAGE_LIMIT}
          type={'number'}
          headerName={'Usage Limit'}
          width={82}
        />
        <DataGrid.Column
          field={COLUMN_CONDITION_REWARDS}
          type={'list'}
          headerName={'Rewards Conditions'}
          unitsName={'Conditions'}
          limit={3}
          width={155}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_GG_CAMPAIGNS}
          type={'list'}
          headerName={'Campaigns'}
          limit={3}
          sortable={false}
          getItemValue={({ id }, _, row) => (
            <ScreenLinkToTracker
              params={{
                filtersCollection: [
                  {
                    period: periodForScreenTracker({
                      startDate: row.activityPeriod[0],
                    }),
                    ...(row.affiliateId && {
                      affiliates: [row.affiliateId],
                    }),
                  },
                ],
                drillPath: [
                  {
                    id: id,
                    type: 'Campaign',
                  },
                ],
              }}
            >
              {id}
            </ScreenLinkToTracker>
          )}
        />
        <DataGrid.Column
          field={COLUMN_ACTIVITY_PERIOD}
          headerName={'Activity Period'}
          width={115}
          type={'dateRange'}
          template={'LL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_REDEEMED}
          type={'text'}
          headerName={'Redeemed'}
          width={101}
          formatter={(value, row) =>
            row.isSet ? (
              <React.Fragment>
                <NumberValueFormatter.Decimal value={row.redeemedPromoCodes} />
                {' of '}
                <NumberValueFormatter.Decimal value={row.usageLimit} />
              </React.Fragment>
            ) : (
              <NumberValueFormatter.Decimal value={value} />
            )
          }
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          type={'text'}
          headerName={'Application'}
          width={160}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          type={'list'}
          headerName={'Platforms'}
          limit={3}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRIES}
          type={'list'}
          headerName={'Countries'}
          limit={3}
          width={160}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_EXCLUDED_COUNTRIES}
          type={'list'}
          headerName={'Excluded Countries'}
          limit={3}
          width={160}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_REQUESTED_BY}
          type={'text'}
          headerName={'Requested by'}
          width={160}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_BY}
          type={'text'}
          headerName={'Last Updated by'}
          width={160}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          type={'text'}
          headerName={'Created by'}
          width={160}
          getValue={({ name }) => name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEditPublicAction}
          onEditSet={handleEditSetAction}
          onDuplicate={handleDuplicateAction}
          limit={3}
          actionsComponent={PromoCodesActions}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default PromoCodesDataTable;
